import React from 'react';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  icon?: string;
  variant?: 'inline' | 'page' | 'fullpage';
  retryLabel?: string;
  errorType?: 'network' | 'server' | 'notFound' | '404' | '500' | 'default';
  illustration?: React.ComponentType<any>;
  useIllustration?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  message,
  onRetry,
  icon = 'lucide:alert-triangle',
  variant = 'page',
  retryLabel,
  errorType,
  illustration: CustomIllustration,
  useIllustration = true,
}) => {
  const { t } = useTranslation();

  const defaultMessage = t('errorLoadingData') || 'دریافت اطلاعات با مشکل مواجه شد';
  const defaultRetryLabel = retryLabel || t('retry') || 'تلاش مجدد';

  const containerClasses = {
    inline: 'flex flex-col items-center justify-center min-h-[200px] p-6',
    page: 'flex flex-col items-center justify-center min-h-[400px] p-8',
    fullpage: 'flex flex-col items-center justify-center h-screen p-8',
  };

  const iconSizes = {
    inline: 48,
    page: 64,
    fullpage: 80,
  };

  const illustrationSizes = {
    inline: { width: 200, height: 150 },
    page: { width: 280, height: 210 },
    fullpage: { width: 360, height: 270 },
  };

  // Smart illustration selection based on error type
  const getIllustrationComponent = (): React.ComponentType<any> | null => {
    if (CustomIllustration) return CustomIllustration;
    if (!useIllustration) return null;

    switch (errorType) {
      case 'network':
        return NetworkError;
      case 'server':
      case '500':
        return Error500;
      case 'notFound':
      case '404':
        return Error404;
      default:
        return null; // Fall back to icon
    }
  };

  const IllustrationComponent = getIllustrationComponent();

  return (
    <div className={containerClasses[variant]}>
      <div className="flex flex-col items-center gap-4 text-center max-w-md">
        {IllustrationComponent ? (
          <div className="w-full max-w-sm">
            <IllustrationComponent {...illustrationSizes[variant]} />
          </div>
        ) : (
          <div className="relative">
            <div className="absolute inset-0 bg-danger-50 dark:bg-danger-900/20 rounded-full blur-xl opacity-50 animate-pulse" />
            <Icon
              icon={icon}
              width={iconSizes[variant]}
              className="text-danger relative"
            />
          </div>
        )}

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-default-900">
            {message || defaultMessage}
          </h3>
          <p className="text-sm text-default-500">
            {t('pleaseTryAgain') || 'لطفاً دوباره تلاش کنید'}
          </p>
        </div>

        {onRetry && (
          <Button
            color="danger"
            variant="flat"
            startContent={<Icon icon="lucide:refresh-cw" width={18} />}
            onClick={onRetry}
          >
            {defaultRetryLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ErrorState;
